import React, { useState, useEffect, useRef } from "react";
import "./test_hire.css";
import { storage, db } from "../../firebase";
import { ref as storageRef, listAll, getDownloadURL, uploadString } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import html2canvas from "html2canvas";
import { useAuth } from "../../context/AuthContext";
import { doc, getDoc, updateDoc } from "firebase/firestore";

export default function BiologyHiringTest() {
  const navigate = useNavigate();
  const testContainerRef = useRef(null);
  const videoRef = useRef(null);
  const fullscreenActive = useRef(false);

  // Test State
  const [timeLeft, setTimeLeft] = useState(1 * 60 * 60);
  const [currentQues, setCurrentQues] = useState(0);
  const [answers, setAnswers] = useState({});
  const [marked, setMarked] = useState([]);
  const [visited, setVisited] = useState([]);
  const [questions, setQuestions] = useState([]);

  // UI Flow State
  const [showInstructions, setShowInstructions] = useState(false);
  const [showTest, setShowTest] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [agreeChecked, setAgreeChecked] = useState(false);
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [showFSPrompt, setShowFSPrompt] = useState(false);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [questionLoading, setQuestionLoading] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);

  // Proctoring & Anti-Cheat State
  const [stream, setStream] = useState(null);
  const [tabSwitchCount, setTabSwitchCount] = useState(0);
  const [isDisqualified, setIsDisqualified] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { currentUser, signInWithGoogle } = useAuth();
  const userName = currentUser?.displayName || currentUser?.email;

  const testName = "Employee Hiring Test - Biology";
  const numberQuestions = 60;
  const durationInMinutes = 60;
  const STORAGE_FOLDER = "TestSeries/biology_hiring";

  const answerKey = {
    1: "C", 2: "A", 3: "C", 4: "D", 5: "D", 6: "D", 7: "C", 8: "B", 9: "D", 10: "D",
    11: "C", 12: "C", 13: "D", 14: "D", 15: "B", 16: "D", 17: "A", 18: "B", 19: "B", 20: "C",
    21: "B", 22: "D", 23: "A", 24: "B", 25: "B", 26: "C", 27: "D", 28: "A", 29: "C", 30: "B",
    31: "B", 32: "C", 33: "D", 34: "C", 35: "A", 36: "D", 37: "D", 38: "D", 39: "B", 40: "C",
    41: "B", 42: "B", 43: "A", 44: "D", 45: "B", 46: "C", 47: "A", 48: "B", 49: "A", 50: "D",
    51: "C", 52: "D", 53: "C", 54: "C", 55: "B", 56: "B", 57: "A", 58: "A", 59: "B", 60: "A"
  };

  // Responsive
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth > 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Track visited questions
  useEffect(() => {
    if (questions.length > 0 && questions[currentQues]) {
      const id = questions[currentQues].id;
      setVisited(prev => prev.includes(id) ? prev : [...prev, id]);
    }
  }, [currentQues, questions]);

  // Clear spinner for non-image questions
  useEffect(() => {
    if (!questionLoading) return;
    const q = questions[currentQues];
    if (q && !q.img) {
      const id = requestAnimationFrame(() => setQuestionLoading(false));
      return () => cancelAnimationFrame(id);
    }
  }, [currentQues, questionLoading, questions]);

  const isImageCached = (url) => {
    if (!url) return true;
    const img = new Image();
    img.src = url;
    return img.complete && img.naturalWidth > 0;
  };

  // Hide navbar during test
  useEffect(() => {
    if (showTest || showResult) {
      document.body.classList.add('test-fullscreen-mode');
    } else {
      document.body.classList.remove('test-fullscreen-mode');
    }
    return () => document.body.classList.remove('test-fullscreen-mode');
  }, [showTest, showResult]);

  // --- 1. PROCTORING: Camera & Tab Switch Logic ---
  useEffect(() => {
    if (showTest && !showResult) {
      const startCamera = async () => {
        try {
          const s = await navigator.mediaDevices.getUserMedia({ video: true });
          setStream(s);
          if (videoRef.current) videoRef.current.srcObject = s;
        } catch (err) {
          alert("Camera access is mandatory for this proctored exam. Access denied.");
          navigate(-1);
        }
      };
      startCamera();
    }
    return () => { if (stream) stream.getTracks().forEach(t => t.stop()); };
  }, [showTest, showResult]);

  // Periodic screenshot every 2 minutes
  useEffect(() => {
    let screenshotInterval;
    if (showTest && !showResult && !isDisqualified) {
      screenshotInterval = setInterval(() => takeScreenshot("PERIODIC_CHECK"), 120000);
    }
    return () => clearInterval(screenshotInterval);
  }, [showTest, showResult, isDisqualified]);

  useEffect(() => {
    if (showTest && !showResult && !isDisqualified) {
      const handleVisibilityChange = () => {
        if (document.hidden) {
          const newCount = tabSwitchCount + 1;
          setTabSwitchCount(newCount);
          takeScreenshot(`MISCONDUCT_STRIKE_${newCount}`);
          if (newCount >= 2) {
            setIsDisqualified(true);
            alert("DISQUALIFIED: Final warning ignored. Test is being submitted automatically.");
            handleSubmit(true);
          } else {
            alert("WARNING: Tab switching/Misconduct detected! (Strike 1/2). Your next attempt will end the test immediately.");
          }
        }
      };
      document.addEventListener("visibilitychange", handleVisibilityChange);
      return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
    }
  }, [showTest, showResult, tabSwitchCount, isDisqualified]);

  const takeScreenshot = async (type = "AUTO") => {
    if (testContainerRef.current) {
      try {
        const canvas = await html2canvas(testContainerRef.current);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.5);
        const path = `Proctoring/${testName}/${currentUser.uid}/${type}_${Date.now()}.jpg`;
        await uploadString(storageRef(storage, path), dataUrl, 'data_url');
      } catch (e) { console.error("Snapshot error:", e); }
    }
  };

  // --- 2. DATA FETCHING ---
  useEffect(() => {
    let cancelled = false;
    async function fetchImages() {
      setLoadingQuestions(true);
      try {
        const folderRef = storageRef(storage, STORAGE_FOLDER + "/");
        const res = await listAll(folderRef);
        const items = await Promise.all(res.items.map(async (itemRef) => {
          const id = itemRef.name.match(/(\d+)/) ? parseInt(itemRef.name.match(/(\d+)/)[1], 10) : null;
          const url = await getDownloadURL(storageRef(storage, `${STORAGE_FOLDER}/${itemRef.name}`));
          const ak = id && answerKey[id] ? String(answerKey[id]).trim() : null;
          return { id, img: url, type: ak && ["A","B","C","D"].includes(ak) ? "mcq" : "numerical", correctAnswer: ak || "" };
        }));
        if (!cancelled) setQuestions(items.filter(it => it.id !== null).sort((a, b) => a.id - b.id));
      } catch (err) { if (!cancelled) setQuestions([]); }
      finally { if (!cancelled) setLoadingQuestions(false); }
    }
    fetchImages();
    return () => { cancelled = true; };
  }, []);

  // Preload all images into browser cache
  useEffect(() => {
    if (questions.length === 0) return;
    questions.forEach((q) => {
      if (q.img) { const img = new Image(); img.src = q.img; }
    });
  }, [questions]);

  // Timer
  useEffect(() => {
    if (showTest && !showResult) {
      const timer = setInterval(() => { setTimeLeft(p => (p > 0 ? p - 1 : 0)); }, 1000);
      return () => clearInterval(timer);
    }
  }, [showTest, showResult]);

  const formatTime = (sec) => {
    const h = String(Math.floor(sec / 3600)).padStart(2, "0");
    const m = String(Math.floor((sec % 3600) / 60)).padStart(2, "0");
    const s = String(sec % 60).padStart(2, "0");
    return `${h}:${m}:${s}`;
  };

  // --- Fullscreen helpers ---
  const requestTestFullscreen = () => {
    fullscreenActive.current = true;
    const el = document.documentElement;
    const req = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen;
    if (req) req.call(el).catch(() => {});
  };
  const exitTestFullscreen = () => {
    fullscreenActive.current = false;
    const exit = document.exitFullscreen || document.webkitExitFullscreen || document.mozCancelFullScreen || document.msExitFullscreen;
    if (exit && (document.fullscreenElement || document.webkitFullscreenElement)) exit.call(document).catch(() => {});
  };
  useEffect(() => {
    const onFSChange = () => {
      if (!document.fullscreenElement && !document.webkitFullscreenElement && fullscreenActive.current) {
        setShowFSPrompt(true);
      }
    };
    document.addEventListener('fullscreenchange', onFSChange);
    document.addEventListener('webkitfullscreenchange', onFSChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFSChange);
      document.removeEventListener('webkitfullscreenchange', onFSChange);
    };
  }, []);

  // --- Image zoom ---
  const handleImageClick = (src) => { setZoomedImage(src); document.body.style.overflow = 'hidden'; };
  const handleCloseZoom = () => { setZoomedImage(null); document.body.style.overflow = 'auto'; };

  // --- 3. TEST HANDLERS ---
  const goToQuestion = (idx) => {
    const q = questions[idx];
    if (q && q.img && !isImageCached(q.img)) setQuestionLoading(true);
    setCurrentQues(idx);
  };

  const handleAnswerChange = (val) => setAnswers({ ...answers, [questions[currentQues].id]: val });

  const handleSaveNext = () => { if (currentQues < questions.length - 1) goToQuestion(currentQues + 1); };

  const handleClear = () => {
    const newAnswers = { ...answers };
    delete newAnswers[questions[currentQues].id];
    setAnswers(newAnswers);
  };

  const handleMarkReview = () => {
    if (!questions[currentQues]) return;
    if (!marked.includes(questions[currentQues].id)) setMarked([...marked, questions[currentQues].id]);
    handleSaveNext();
  };

  const handleMarkReviewOnly = () => {
    if (!questions[currentQues]) return;
    const newAnswers = { ...answers };
    delete newAnswers[questions[currentQues].id];
    setAnswers(newAnswers);
    if (!marked.includes(questions[currentQues].id)) setMarked([...marked, questions[currentQues].id]);
    handleSaveNext();
  };

  const handleBack = () => { if (currentQues > 0) goToQuestion(currentQues - 1); };
  const handleNext = () => { if (currentQues < questions.length - 1) goToQuestion(currentQues + 1); };

  const handleSubmit = async (forced = false) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    await takeScreenshot(forced ? "FORCED_DISQUALIFIED" : "FINAL_SUBMIT");
    exitTestFullscreen();
    setShowTest(false);
    setShowResult(true);

    let att = 0, corr = 0, incorr = 0;
    const details = questions.map(q => {
      const ans = answers[q.id] || null;
      const isCorr = ans !== null && String(ans).trim() === String(q.correctAnswer).trim();
      if (ans) { att++; isCorr ? corr++ : incorr++; }
      return { questionId: q.id, userAnswer: ans, correctAnswer: q.correctAnswer, status: ans === null ? 'unattempted' : isCorr ? 'correct' : 'incorrect' };
    });

    if (currentUser) {
      const userRef = doc(db, "users", currentUser.uid);
      try {
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          let testsTaken = userData.testsTaken || [];
          let newAttempt = 1;
          const existingTestIndex = testsTaken.findIndex((t) => t.testName === testName);
          if (existingTestIndex !== -1) {
            newAttempt = (testsTaken[existingTestIndex].attempt || 1) + 1;
            testsTaken.splice(existingTestIndex, 1);
          }
          const resultData = {
            testName,
            score: (corr * 4) - (incorr * 1),
            misconduct: forced || tabSwitchCount >= 2,
            attempted: att,
            correct: corr,
            incorrect: incorr,
            total: questions.length * 4,
            totalQuestions: questions.length,
            date: new Date().toISOString(),
            attempt: newAttempt,
            storagePath: STORAGE_FOLDER,
            questionDetails: details,
          };
          testsTaken.push(resultData);
          await updateDoc(userRef, { testsTaken });
        }
      } catch (err) { console.error("Error updating result:", err); }
    }
  };

  const handleGoogleLogin = async () => {
    try {
      await signInWithGoogle();
      setShowLoginPopup(false);
      setShowInstructions(true);
    } catch (error) {
      const cancelled = error?.code === 'auth/popup-closed-by-user' || error?.code === 'auth/cancelled-popup-request';
      if (!cancelled) console.error("Login failed", error);
    }
  };

  // --- 4. UI SECTIONS ---

  if (loadingQuestions) return <div className="initial-page-container"><div className="test-info-box"><h2>Loading Security Modules...</h2><p>Fetching questions from server. Please wait.</p></div></div>;

  // Login popup
  if (showLoginPopup) return (
    <div className="modal-backdrop" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
      <div className="login-modal" style={{ backgroundColor: '#fff', padding: '30px', borderRadius: '8px', textAlign: 'center', maxWidth: '400px', width: '90%', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
        <h3 style={{ color: '#ef4444', marginBottom: '20px' }}>Login Required</h3>
        <p style={{ marginBottom: '30px' }}>Please log in with Google to start the test and save your progress.</p>
        <div className="button-row">
          <button onClick={handleGoogleLogin} className="start-test-btn" style={{ backgroundColor: '#4285F4', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '10px' }}>🔑 Login with Google</button>
          <button onClick={() => setShowLoginPopup(false)} className="back-btn" style={{ backgroundColor: '#6b7280', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
        </div>
      </div>
    </div>
  );

  if (!showInstructions && !showTest && !showResult) return (
    <div className="initial-page-container">
      <div className="test-info-box">
        <h2>{testName}</h2>
        <p><strong>Questions:</strong> {numberQuestions} &nbsp;|&nbsp; <strong>Duration:</strong> {durationInMinutes} Minutes</p>
        <p><strong>Marking:</strong> +4 correct, &minus;1 incorrect, 0 unattempted</p>
        <p style={{ color: '#d32f2f', fontWeight: 'bold' }}>⚠️ Proctored Exam. Camera &amp; screenshots required.</p>
        <p><strong>Secure Mode Active.</strong></p>
        <div className="button-row">
          <button className="start-test-btn" onClick={() => (currentUser ? setShowInstructions(true) : setShowLoginPopup(true))}>Start Test</button>
          <button className="back-btn" onClick={() => navigate(-1)}>Back</button>
        </div>
      </div>
    </div>
  );

  if (showInstructions) return (
    <div className="instructions-container">
      <h2 style={{ color: '#d32f2f' }}>⚠️ Mandatory Exam Rules</h2>
      <div className="instruction-box" style={{ borderLeft: '5px solid #d32f2f', backgroundColor: '#fff5f5' }}>
        <p><strong>1. Remote Proctoring:</strong> Your camera must remain ON. AI-monitored snapshots are captured periodically.</p>
        <p><strong>2. Zero Tolerance Policy:</strong> Any attempt to <strong>switch tabs</strong> or minimize the browser will be flagged.</p>
        <p><strong>3. Fullscreen:</strong> The test will run in fullscreen. Exiting fullscreen will prompt you to return.</p>
        <p><strong>4. Environment:</strong> Ensure high-speed internet and a well-lit room.</p>
        <p><strong>5. Auto-Submit:</strong> Leaving the test tab <strong>twice</strong> will result in immediate disqualification.</p>
        <p style={{ fontWeight: 'bold', marginTop: '15px', color: '#b91c1c' }}>IF CAUGHT CHEATING OR SWITCHING TABS, YOU WILL BE DISQUALIFIED IMMEDIATELY.</p>
      </div>
      <div className="agree-section">
        <label>
          <input type="checkbox" checked={agreeChecked} onChange={(e) => setAgreeChecked(e.target.checked)} />
          {" "}I confirm my camera works and I accept the terms of disqualification.
        </label>
        <button className="proceed-btn" disabled={!agreeChecked} onClick={() => { requestTestFullscreen(); setShowInstructions(false); setShowTest(true); }}>
          Start Exam ➡
        </button>
      </div>
    </div>
  );

  if (showTest) {
    const q = questions[currentQues];
    return (
      <div className="mock-container" ref={testContainerRef}>
        {/* Fullscreen return prompt */}
        {showFSPrompt && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.92)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px' }}>
            <p style={{ color: '#fff', fontSize: '1.2rem', textAlign: 'center', margin: '0 20px' }}>⚠️ Fullscreen is required during the test.<br/>Please return to fullscreen to continue.</p>
            <button onClick={() => { requestTestFullscreen(); setShowFSPrompt(false); }} style={{ padding: '14px 36px', fontSize: '1rem', fontWeight: 600, background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>🔲 Return to Fullscreen</button>
          </div>
        )}
        {/* Image zoom modal */}
        {zoomedImage && (
          <div className="image-modal-overlay">
            <div className="image-modal-backdrop" onClick={handleCloseZoom} />
            <div className="image-modal-content">
              <button className="image-modal-close" onClick={handleCloseZoom}>✕</button>
              <img src={zoomedImage} alt="Question Full View" className="image-modal-img" />
            </div>
          </div>
        )}
        {/* Proctor webcam overlay */}
        <div className="proctor-overlay">
          <video ref={videoRef} autoPlay playsInline muted />
          <span>Monitoring ON</span>
        </div>
        {/* Sticky header */}
        <div className="test-header-forced">
          <div className="header-left-info">
            <span className="user-emoji">👤</span>
            <div className="user-details">
              <div className="info-line"><span className="label-bold">Candidate:</span><span className="value-text">{userName || "Guest"}</span></div>
              <div className="info-line"><span className="label-bold">Test:</span><span className="value-text">{testName}</span></div>
              <div className="info-line">
                <span className="label-bold" style={{ color: tabSwitchCount > 0 ? '#ff6b6b' : '#90EE90' }}>
                  {tabSwitchCount > 0 ? `⚠️ STRIKE ${tabSwitchCount}/2` : '🔒 Secure Mode'}
                </span>
              </div>
            </div>
          </div>
          <div className="header-timer-section">
            <div className="timer-label">Remaining Time:</div>
            <div className="timer-big-value" style={{ color: timeLeft < 600 ? '#f44336' : '#fff' }}>{formatTime(timeLeft)}</div>
            <div className="timer-total">Duration: {durationInMinutes} min</div>
          </div>
        </div>

        <div className="mock-body">
          <div className="question-area">
            {questionLoading && (
              <div className="question-loading-overlay">
                <div className="question-spinner"></div>
                <p className="question-loading-text">Loading question...</p>
              </div>
            )}
            <div className="question-clickable" onClick={() => q.img && handleImageClick(q.img)} style={q.img ? { cursor: 'zoom-in' } : {}}>
              <h4>Question {q.id}</h4>
              {q.img && (
                <div className="image-section">
                  <div className="question-image-wrapper">
                    <img src={q.img} alt={`Q${q.id}`} onLoad={() => setQuestionLoading(false)} onError={() => setQuestionLoading(false)} />
                  </div>
                </div>
              )}
            </div>

            {q.type === "mcq" && (
              <div style={{ marginTop: '20px', display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
                <span style={{ fontWeight: '600', fontSize: '0.85rem' }}>Options:</span>
                {["A", "B", "C", "D"].map(n => (
                  <label key={n} className="option">
                    <input type="radio" name={`q${q.id}`} value={n} checked={answers[q.id] === n} onChange={(e) => handleAnswerChange(e.target.value)} />
                    {n})
                  </label>
                ))}
              </div>
            )}
            {q.type === "numerical" && (
              <input type="text" inputMode="numeric" className="numeric-input" value={answers[q.id] || ""} onChange={(e) => handleAnswerChange(e.target.value)} placeholder="Enter Numeric Answer" />
            )}

            <div className="button-row">
              <button className="save-btn" onClick={handleSaveNext}>Save &amp; Next</button>
              <button className="clear-btn" onClick={handleClear}>Clear Response</button>
              <button className="mark-only-btn" onClick={handleMarkReviewOnly}>Mark for Review</button>
              <button className="mark-btn" onClick={handleMarkReview}>Answered &amp; Mark</button>
            </div>
            <div className="button-row" style={{ marginTop: '10px' }}>
              <button className="nav-btn" onClick={handleBack}>◀ Back</button>
              <button className="nav-btn" onClick={handleNext}>Next ▶</button>
              <button className="submit-btn" onClick={() => handleSubmit(false)} disabled={isSubmitting}>{isSubmitting ? 'Submitting...' : 'Submit Test'}</button>
            </div>
          </div>

          <div className="palette-col">
            <div className="question-palette">
              <h4>Question Palette</h4>
              <div className="palette-grid">
                {questions.map((qq, i) => {
                  const isAnswered = answers[qq.id] !== undefined && answers[qq.id] !== null && answers[qq.id] !== "";
                  const isMarked = marked.includes(qq.id);
                  const isVisited = visited.includes(qq.id);
                  const isCurrent = currentQues === i;
                  let cls = "palette-btn";
                  if (isAnswered && isMarked) cls += " answered-marked";
                  else if (isAnswered) cls += " answered";
                  else if (isMarked) cls += " marked";
                  else if (!isVisited) cls += " not-visited";
                  if (isCurrent) cls += " active";
                  return <button key={qq.id} className={cls} onClick={() => goToQuestion(i)}>{i + 1}</button>;
                })}
              </div>
              <div className="palette-legend">
                <div className="legend-item"><span className="legend-box not-visited" style={{ background: '#1976D2' }}></span><span>Not Visited</span></div>
                <div className="legend-item"><span className="legend-box" style={{ background: 'white', border: '2px solid #ccc', borderRadius: '3px', display: 'inline-block', width: '16px', height: '16px' }}></span><span>Not Answered</span></div>
                <div className="legend-item"><span className="legend-box answered"></span><span>Answered</span></div>
                <div className="legend-item"><span className="legend-box marked"></span><span>Marked (Not Eval.)</span></div>
                <div className="legend-item"><span className="legend-box answered-marked"></span><span>Answered &amp; Marked (Eval.)</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showResult) {
    let att = 0, corr = 0, incorr = 0;
    questions.forEach(q => {
      const ans = answers[q.id];
      if (ans && ans !== "") {
        att++;
        String(ans).trim() === String(q.correctAnswer).trim() ? corr++ : incorr++;
      }
    });
    return (
      <div className="mock-container">
        <div className="result-header">
          <h2>Test Result &amp; Analysis{isDisqualified && <span className="disqualified-tag"> — DISQUALIFIED FOR MISCONDUCT</span>}</h2>
          <button className="result-back-btn" onClick={() => navigate(-1)}>⬅️ Exit Analysis</button>
        </div>
        <div className="mock-body" style={{ flexDirection: 'column' }}>
          <div className="question-area" style={{ flex: 1 }}>
            <div className="result-summary">
              <p className="result-candidate">👤 Candidate: {userName}</p>
              <div className="summary-cards">
                <div className="card attempted">Attempted: {att}</div>
                <div className="card correct">Correct: {corr}</div>
                <div className="card incorrect">Incorrect: {incorr}</div>
                <div className="card total">Total Questions: {questions.length}</div>
                <div className="card marks">Score: {(corr * 4) - incorr} / {questions.length * 4}</div>
              </div>
            </div>
            <h4 className="detail-heading">Question-wise Details:</h4>
            <div className="question-details" style={{ display: 'grid', gridTemplateColumns: isDesktop ? 'repeat(2, 1fr)' : '1fr', gap: '15px', marginTop: '20px' }}>
              {questions.map((q, i) => (
                <div key={q.id} className={`question-detail ${answers[q.id] === q.correctAnswer ? 'correct' : answers[q.id] ? 'incorrect' : 'unattempted'}`}>
                  <p style={{ fontWeight: '700', marginBottom: 4 }}>Q. {i + 1}</p>
                  {q.img && <div style={{ marginBottom: 8 }}><img src={q.img} alt={`Q${i + 1}`} className="question-detail-img" /></div>}
                  <p>Your Answer: {answers[q.id] || "Not Answered"} | Correct: {q.correctAnswer || "—"}</p>
                  <p>Marks: {answers[q.id] === q.correctAnswer ? "+4" : answers[q.id] ? "-1" : "0"}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}